/**
 * Automation Rules API Client (Chunk 111)
 */

import apiClient from './client';

// ── Types ─────────────────────────────────────────────────────────────────────

export type AutomationTriggerType =
  | 'health_score_below'
  | 'var_exceeds'
  | 'asset_price_below'
  | 'asset_price_above'
  | 'portfolio_drawdown_exceeds';

export type AutomationActionType =
  | 'send_notification'
  | 'create_rebalancing_plan'
  | 'flag_for_review';

export type AutomationExecutionStatus = 'success' | 'failed' | 'skipped';

export interface SendNotificationConfig {
  channels: Array<'in_app' | 'email' | 'webhook'>;
  message?: string;
  severity?: 'info' | 'warning' | 'critical';
}

export interface CreateRebalancingPlanConfig {
  targets?: Array<{ symbol: string; targetPct: number; tolerancePct: number }>;
  note?: string;
}

export interface FlagForReviewConfig {
  reason: string;
  priority?: 'low' | 'medium' | 'high';
}

export type AutomationActionConfig =
  | SendNotificationConfig
  | CreateRebalancingPlanConfig
  | FlagForReviewConfig;

export interface AutomationRule {
  id: string;
  user_id: string;
  name: string;
  trigger_type: AutomationTriggerType;
  trigger_value: number;
  trigger_asset: string | null;
  action_type: AutomationActionType;
  action_config: AutomationActionConfig;
  is_active: boolean;
  last_triggered_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface AutomationExecution {
  id: string;
  rule_id: string;
  triggered_at: string;
  trigger_snapshot: Record<string, unknown>;
  action_taken: string;
  status: AutomationExecutionStatus;
}

export interface CreateAutomationRuleRequest {
  userId: string;
  name: string;
  trigger_type: AutomationTriggerType;
  trigger_value: number;
  trigger_asset?: string;
  action_type: AutomationActionType;
  action_config: AutomationActionConfig;
  is_active?: boolean;
}

export interface UpdateAutomationRuleRequest {
  userId: string;
  name?: string;
  trigger_type?: AutomationTriggerType;
  trigger_value?: number;
  trigger_asset?: string | null;
  action_type?: AutomationActionType;
  action_config?: AutomationActionConfig;
  is_active?: boolean;
}

// ── API Functions ─────────────────────────────────────────────────────────────

export async function getAutomationRules(
  userId: string,
  includeInactive = false
): Promise<AutomationRule[]> {
  const res = await apiClient.get<{ rules: AutomationRule[]; total: number }>(
    `/analytics/automation/rules`,
    { params: { userId, includeInactive: includeInactive ? 'true' : 'false' } }
  );
  return res.data.rules;
}

export async function createAutomationRule(
  data: CreateAutomationRuleRequest
): Promise<AutomationRule> {
  const res = await apiClient.post<{ rule: AutomationRule }>(
    `/analytics/automation/rules`,
    data
  );
  return res.data.rule;
}

export async function updateAutomationRule(
  ruleId: string,
  data: UpdateAutomationRuleRequest
): Promise<AutomationRule> {
  const res = await apiClient.patch<{ rule: AutomationRule }>(
    `/analytics/automation/rules/${ruleId}`,
    data
  );
  return res.data.rule;
}

export async function deleteAutomationRule(ruleId: string, userId: string): Promise<void> {
  await apiClient.delete(`/analytics/automation/rules/${ruleId}`, {
    params: { userId },
  });
}

export async function getAutomationRuleExecutions(
  ruleId: string,
  userId: string,
  limit = 10
): Promise<AutomationExecution[]> {
  const res = await apiClient.get<{ executions: AutomationExecution[] }>(
    `/analytics/automation/rules/${ruleId}/executions`,
    { params: { userId, limit } }
  );
  return res.data.executions;
}

// ── Label Helpers ─────────────────────────────────────────────────────────────

export const TRIGGER_TYPE_LABELS: Record<AutomationTriggerType, string> = {
  health_score_below: 'Health Score Below',
  var_exceeds: 'VaR Exceeds',
  asset_price_below: 'Asset Price Below',
  asset_price_above: 'Asset Price Above',
  portfolio_drawdown_exceeds: 'Portfolio Drawdown Exceeds',
};

export const ACTION_TYPE_LABELS: Record<AutomationActionType, string> = {
  send_notification: 'Send Notification',
  create_rebalancing_plan: 'Create Rebalancing Plan',
  flag_for_review: 'Flag for Review',
};

export function formatTriggerSummary(rule: AutomationRule): string {
  const label = TRIGGER_TYPE_LABELS[rule.trigger_type];
  switch (rule.trigger_type) {
    case 'health_score_below':
      return `${label}: ${rule.trigger_value}`;
    case 'var_exceeds':
      return `${label}: ${rule.trigger_value}%`;
    case 'asset_price_below':
    case 'asset_price_above':
      return `${label}: $${rule.trigger_value.toLocaleString()} (${rule.trigger_asset ?? 'N/A'})`;
    case 'portfolio_drawdown_exceeds':
      return `${label}: ${rule.trigger_value}%`;
    default:
      return label;
  }
}

export function formatActionSummary(rule: AutomationRule): string {
  const label = ACTION_TYPE_LABELS[rule.action_type];
  if (rule.action_type === 'flag_for_review') {
    const config = rule.action_config as FlagForReviewConfig;
    return `${label}: ${config.reason ?? ''}`;
  }
  return label;
}
